 import {
    Component,
    Injectable,
    WritableSignal,
    computed,
    inject,
    signal
} from "@angular/core";

import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogModule,
    MatDialogRef,
} from "@angular/material/dialog";

import {
    lastValueFrom
} from "rxjs";

import {
    BaseDialogService,
    NonNullableConfigData,
    TilbyDialogContentComponent,
    TilbyDialogToolbarComponent,
} from "@tilby/tilby-ui-lib/components/tilby-dialog";

import {
    ConfigurationManagerService,
    EntityManagerService,
    OperatorManagerService
} from "src/app/core";

import {
    Departments,
    SaleBase,
    Sales,
    SalesItems,
    SalesPriceChanges,
    SaleTransactions
} from "tilby-models";

import {
    SaleTransactionUtilsService,
    SaleUtilsService
} from "src/app/features";

import {
    SalesCashregister,
    SplitSaleData,
    SplitSaleRootItemsData
} from "src/app/shared/model/cashregister.model";

import {
    MathUtils,
    maxBy,
    removeFromArray
} from "src/app/shared/utils";

import {
    TranslateModule,
    TranslateService
} from "@ngx-translate/core";

import {
    v4 as generateUuid,
    validate as validateUuid
} from 'uuid';

import {
    PromptDialogService
} from "../prompt-dialog/prompt-dialog.component";

import {
    keyBy
} from 'src/app/shared/utils';

import {
    MatButtonModule
} from "@angular/material/button";

import {
    MatIconModule
} from "@angular/material/icon";

import {
    MatFormFieldModule
} from "@angular/material/form-field";

import {
    MatInputModule
} from "@angular/material/input";

import {
    FormsModule
} from "@angular/forms";

import {
    TilbyCurrencyPipe
} from "@tilby/tilby-ui-lib/pipes/tilby-currency";

import {
    MatSelectModule
} from "@angular/material/select";

import {
    GeneralInputButton
} from "@tilby/tilby-ui-lib/models";

import {
    MatDividerModule
} from "@angular/material/divider";

import {
    CommonModule
} from "@angular/common";

import {
    TilbyGesturesDirective
} from "@tilby/tilby-ui-lib/directives/tilby-gestures";

import {
    ItemDetailsDialogService
} from "./item-details-dialog/item-details-dialog.component";

import {
    CdkFixedSizeVirtualScroll,
    CdkVirtualForOf,
    CdkVirtualScrollViewport
} from "@angular/cdk/scrolling";

import {
    AlertDialogService
} from 'src/app/dialogs';

import {
    mobileCheck
} from "src/utilities";

type SplitSaleDialogData = {
    currentSale: SalesCashregister,
    options?: SplitSaleDialogOptions
};

export type SplitSaleDialogOptions = {
    type?: 'by_covers' | 'by_amount' | 'by_items',
    amount?: number,
    covers?: number,
    department?: Departments | null,
    splitIndex?: number,
    itemName?: string
};

type SplitSaleReturnValue = {
    originalSale: SalesCashregister,
    splitSale: SalesCashregister,
} | {
    originalSaleTransaction: SaleTransactions;
    splitSale?: SalesCashregister
}

const rootItemsRoundingDigits = 8;

const dutchTreatGroupUuidPrefix = '00000000-0000-4000-a000-';
const dutchTreatGroupUuidPlaceholder = '00000000-0000-4000-a000-000000000000';

@Component({
    selector: 'app-split-sale-dialog',
    templateUrl: './split-sale-dialog.component.html',
    styleUrls: ['./split-sale-dialog.component.scss'],
    standalone: true,
    imports: [
        CdkFixedSizeVirtualScroll,
        CdkVirtualScrollViewport,
        CdkVirtualForOf,
        CommonModule,
        FormsModule,
        MatButtonModule,
        MatDialogModule,
        MatDividerModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatSelectModule,
        TilbyCurrencyPipe,
        TilbyDialogContentComponent,
        TilbyDialogToolbarComponent,
        TilbyGesturesDirective,
        TranslateModule,
    ]
})
export class SplitSaleDialogComponent {
    private readonly matDialogRef = inject(MatDialogRef);
    private readonly saleUtilsService = inject(SaleUtilsService);
    private readonly translateService = inject(TranslateService);
    private readonly promptDialogService = inject(PromptDialogService);
    private readonly entityManagerService = inject(EntityManagerService);
    private readonly operatorManagerService = inject(OperatorManagerService);
    private readonly saleTransactionUtils = inject(SaleTransactionUtilsService);
    private readonly configurationManagerService = inject(ConfigurationManagerService);
    private readonly itemDetailsDialogService = inject(ItemDetailsDialogService);
    private readonly alertDialogService = inject(AlertDialogService);

    protected readonly data: SplitSaleDialogData = inject(MAT_DIALOG_DATA);

    private sale = this.data.currentSale;

    protected options: SplitSaleDialogOptions = this.data.options ?? {
        department: null
    };

    protected departments: Departments[] = [];
    protected dialogMode: WritableSignal<'type_select' | 'type_options' | 'sale_view'> = signal("type_select");
    protected disableAmountAndCovers = this.configurationManagerService.getPreference('cashregister.disable_split_sale_amount_covers');
    protected disableBack = false;
    protected hideAsterisk = false;
    protected splitItemsOnDutchTreat = !!this.configurationManagerService.getPreference('pienissimo.token') || !!this.configurationManagerService.getPreference('cashregister.split_sale.split_items_on_dutch_treat');
    protected dutchTreatSplitMode = false;
    protected originalSale?: SalesCashregister;
    protected showPrices = this.configurationManagerService.isUserPermitted('show_prices_ui');
    protected splitIndex = (Number(this.sale?._split_sale_data?.split_index) || 0) + 1;
    protected splitSale?: SalesCashregister;
    protected saleAmount = this.sale.final_amount || 0;

    private canConfirmSale = signal(false);
    protected splitSaleAmount = signal(0);
    protected originalSaleAmount = signal(0);

    protected originalQuantitiesByUuid: Record<string, number> = {};

    protected customActions: GeneralInputButton[] = [
        {
            isIt: computed(() => this.dialogMode() === 'type_options'),
            isDisable: () => !this.canConfirmSplit(),
            name: "",
            icon: signal("check"),
            click: () => this.confirmSplitOptions(),
        },
        {
            isIt: computed(() => this.dialogMode() === 'sale_view'),
            isDisable: computed(() => !this.canConfirmSale()),
            name: "",
            icon: signal("check"),
            click: () => this.confirm(),
        },
    ];

    protected virtualScrollClasses = mobileCheck() ? 'tw-h-full' : 'tw-h-[300px] tw-h-max-[300px]';
    
    async ngOnInit() {
        this.departments = await this.entityManagerService.departments.fetchCollectionOffline();

        //Force split by items if the sale has root items
        if(this.getRootItemsData(this.sale)) {
            this.options.type = 'by_items';
        }

        if (this.options.type) {
            this.disableBack = true;

            if (this.options.type === 'by_items') {
                this.splitByItems();
            } else {
                this.splitByCoversOrAmount(this.options.type);
            }
        }
    }

    protected isRootItem(saleItemUuid?: string): boolean {
        return !!saleItemUuid?.startsWith(dutchTreatGroupUuidPrefix);
    }

    getNewSaleName(currentName: string) {
        const splitSuffix = ` - ${this.splitIndex}`;

        return currentName.slice(0, 30 - splitSuffix.length) + splitSuffix;
    }

    updateNewSaleData(newSale: SalesCashregister) {
        if (!this.originalSale) {
            return;
        }

        const opData = this.operatorManagerService.getOperatorData();
        const dateNow = new Date();

        Object.assign(newSale, {
            name: this.getNewSaleName(this.originalSale.name),
            open_at: dateNow.toISOString(),
            sale_parent_uuid: this.originalSale.uuid,
            split_sale_origin_uuid: this.originalSale.split_sale_origin_uuid || this.originalSale.uuid,
            seller_id: opData.id,
            seller_name: opData.full_name,
            status: "open",
            uuid: generateUuid()
        });

        if (!validateUuid(`${this.originalSale.id}`)) {
            newSale.sale_parent_id = this.originalSale.id;
        }

        if (!newSale.sale_items) {
            return;
        }

        for (const saleItem of newSale.sale_items) {
            this.updateNewSaleItemData(saleItem);
        }
    }

    updateNewSaleItemData(saleItem: SalesItems) {
        const opData = this.operatorManagerService.getOperatorData();
        const dateNow = new Date().toISOString();

        Object.assign(saleItem, {
            added_at: dateNow,
            lastupdate_at: dateNow,
            lastupdate_by: opData.id,
            seller_id: opData.id,
            seller_name: opData.full_name,
            uuid: generateUuid()
        });
    }

    updateSalePricesWithRootItems(sale?: SalesCashregister) {
        if(!sale) {
            return;
        }

        sale.sale_items = sale.sale_items ?? [];
        const rootItems = removeFromArray(sale.sale_items, (si) => this.isRootItem(si.uuid));

        this.saleUtilsService.calculateSalePrices(sale);

        for(const rootItem of rootItems) {
            rootItem.final_row_price = MathUtils.round(rootItem.price * rootItem.quantity);
        }

        sale.sale_items.push(...rootItems);
    }

    updateSalePrices() {
        this.updateSalePricesWithRootItems(this.originalSale);
        this.updateSalePricesWithRootItems(this.splitSale);

        const originalSaleAmount = this.visibleItems(this.originalSale).reduce((acc, si) => MathUtils.round(acc + (si.final_row_price || 0)), 0) || 0;
        const splitSaleAmount = this.visibleItems(this.splitSale).reduce((acc, si) => MathUtils.round(acc + (si.final_row_price || 0)), 0) || 0;

        this.originalSaleAmount.set(originalSaleAmount);
        this.splitSaleAmount.set(splitSaleAmount);

        const hasItemsInSplitSale = !!this.splitSale?.sale_items?.some((saleItem) => saleItem.quantity !== 0);
        this.canConfirmSale.set(hasItemsInSplitSale);
    }

    removeSaleItemRelations(sale: SalesCashregister) {
        if (!sale.sale_items) {
            return;
        }

        for (const saleItem of sale.sale_items) {
            if (this.isRootItem(saleItem.sale_item_parent_uuid)) {
                saleItem.sale_item_parent_uuid = undefined;
            }
        }
    }

    private getSplitSale(originalSale: SalesCashregister) {
        const saleItemSubEntities: (keyof SalesItems)[] = ['price_changes', 'variations', 'ingredients'];
        const saleFieldsToClean: (keyof SalesCashregister)[] = ['id', 'sale_customer', 'closed_at', 'created_at', 'createdby_id', 'deleted_at', 'deletedby_id', 'updated_at', 'updatedby_id', '_split_sale_data'];
        const saleItemFieldsToClean: (keyof SalesItems)[] = ['id', 'sale_id', 'created_at', 'deleted_at', 'updated_at'];
        const saleItemSubFieldsToClean: ('id' | 'sale_item_id')[] = ['id', 'sale_item_id'];

        const splitSale = structuredClone(originalSale);

        //Cleanup backend-related fields from the split sale
        for (const field of saleFieldsToClean) {
            delete splitSale[field];
        }

        splitSale.split_sale_origin_uuid = originalSale.split_sale_origin_uuid || originalSale.uuid;

        //Cleanup sale documents and payments from split sale
        splitSale.sale_documents = [];
        splitSale.payments = [];

        const priceChanges = splitSale.price_changes = splitSale.price_changes || [];
        const saleItems = splitSale.sale_items = splitSale.sale_items || [];

        //Cleanup backend-related fields from split sale price changes
        for (const priceChange of priceChanges) {
            for (const field of saleItemFieldsToClean) {
                delete priceChange[<keyof SalesPriceChanges>field];
            }
        }

        //Cleanup backend-related fields from split sale items
        for (const saleItem of saleItems) {
            //Delete backend-related fields
            for (const field of saleItemFieldsToClean) {
                delete saleItem[field];
            }

            for (const subEntityName of saleItemSubEntities) {
                const subEntityArr = saleItem[subEntityName];

                if (!Array.isArray(subEntityArr)) {
                    continue;
                }

                for (const subEntity of subEntityArr) {
                    for (const field of saleItemSubFieldsToClean) {
                        delete subEntity[field];
                    }
                }
            }

            saleItem.quantity = 0;
        }

        return splitSale;
    }

    prepareSplitSale() {
        if (!this.originalSale) {
            return;
        }

        //Cleanup price changes from original sale
        const priceChangesToKeep = new Set(['discount_perc', 'surcharge_perc', 'gift']); //Keep percent-based price changes
        this.originalSale.price_changes = this.originalSale.price_changes?.filter((priceChange) => priceChangesToKeep.has(priceChange.type)) || [];

        //Initialize sale_items array if necessary
        this.originalSale.sale_items = this.originalSale.sale_items ?? [];

        for (const saleItem of this.originalSale.sale_items) {
            //Initialize sale item subentities if necessary
            saleItem.price_changes = saleItem.price_changes?.filter((priceChange) => priceChangesToKeep.has(priceChange.type)) ?? [];
            saleItem.variations = saleItem.variations ?? [];
            saleItem.ingredients = saleItem.ingredients ?? [];
        }

        // Create root item if we are using dutch treat with items split
        let rootItemsData = this.getRootItemsData(this.originalSale);

        // Update original sale prices
        this.saleUtilsService.calculateSalePrices(this.originalSale);

        if(!rootItemsData && this.splitItemsOnDutchTreat && this.options.type !== 'by_items') {
            const saleAmount = this.originalSale.final_amount || 0;
            const { percentages } = this.saleUtilsService.getSaleItemsRepartitionPercentages(this.options.type!, saleAmount, this.options.type === 'by_covers' ? this.options.covers! : this.options.amount!, { decimals: rootItemsRoundingDigits });

            rootItemsData = {
                name: this.options.itemName!,
                price: saleAmount,
                quantities: percentages,
            };

            for (const si of this.originalSale.sale_items) {
                si.original_quantity = si.quantity;
            }
        } else {
            for (const si of this.originalSale.sale_items) {
                si.original_quantity = si.original_quantity ?? si.quantity;
            }
        }

        if(rootItemsData) {
            const rootItems: SalesItems[] = rootItemsData.quantities.map((quantity, i) => ({
                ...this.saleUtilsService.getDynamicSaleItemTemplate(this.options.department || this.departments[0], rootItemsData.price),
                name: rootItemsData.name,
                quantity: quantity / 100,
                is_group_item: true,
                uuid: `${dutchTreatGroupUuidPrefix}${i.toString().padStart(12, '0')}`,
            }));

            for(const saleItem of this.originalSale.sale_items) {
                saleItem.sale_item_parent_uuid = dutchTreatGroupUuidPlaceholder;
                this.originalQuantitiesByUuid[saleItem.uuid] = saleItem.quantity;
                saleItem.quantity = saleItem.original_quantity || saleItem.quantity;
            }

            this.originalSale.sale_items.push(...rootItems);
        }

        this.dutchTreatSplitMode = !!rootItemsData;

        //Create the split sale as a copy of the original sale
        this.splitSale = this.getSplitSale(this.originalSale);

        this.updateSalePrices();

        this.dialogMode.set('sale_view');
    }

    doMoveSaleItem(srcSaleItem: SalesItems, dstSaleItem: SalesItems, quantityPerc?: number) {
        if (!srcSaleItem.quantity || !dstSaleItem) {
            return;
        }

        if (quantityPerc) {
            const totalQuantity = MathUtils.round(srcSaleItem.quantity + dstSaleItem.quantity, 3);

            dstSaleItem.quantity = Math.round(totalQuantity * quantityPerc);

            srcSaleItem.quantity = MathUtils.round(totalQuantity - dstSaleItem.quantity, 3);
        } else if (srcSaleItem.quantity % 1) {
            dstSaleItem.quantity = srcSaleItem.quantity;
            srcSaleItem.quantity = 0;
        } else {
            dstSaleItem.quantity++;
            srcSaleItem.quantity--;
        }
    }

    moveSaleItem(saleItem: SalesItems, sourceSale?: SalesCashregister, destinationSale?: SalesCashregister) {
        if (!sourceSale || !destinationSale) {
            return;
        }

        // If we are in split mode and the item is NOT a root item, do nothing
        if (this.dutchTreatSplitMode && !this.isRootItem(saleItem.uuid)) {
            return;
        }

        const destinationSaleItemsByUuid = keyBy(destinationSale.sale_items || [], (si) => si.uuid);

        const targetItem = destinationSaleItemsByUuid[saleItem.uuid];

        if (!targetItem) {
            return;
        }

        // Move the item (whether it's a root item or a normal item in standard mode)
        this.doMoveSaleItem(saleItem, targetItem);

        // Handle children ONLY if we are NOT in dutch treat split mode, as those are handled on confirm
        if (!this.dutchTreatSplitMode) {
            const relatedItems = sourceSale.sale_items?.filter(si => si.sale_item_parent_uuid && si.sale_item_parent_uuid === saleItem.uuid) || [];

            if (relatedItems.length) {
                const srcQty = saleItem.quantity;
                const destQty = destinationSaleItemsByUuid[saleItem.uuid]?.quantity || 0;

                const quantityPerc = destQty / (destQty + srcQty);

                for (const childSaleItem of relatedItems) {
                    this.doMoveSaleItem(childSaleItem, destinationSaleItemsByUuid[childSaleItem.uuid], quantityPerc);
                }
            }
        }

        //Update sale prices
        this.updateSalePrices();
    }

    moveToSplitSale(saleItem: SalesItems) {
        this.moveSaleItem(saleItem, this.originalSale, this.splitSale);
    }

    moveToOriginalSale(saleItem: SalesItems) {
        this.moveSaleItem(saleItem, this.splitSale, this.originalSale);
    }

    visibleItems(sale?: SalesCashregister) {
        return this.dutchTreatSplitMode
            ? sale?.sale_items?.filter((saleItem) => (saleItem.quantity > 0) && this.isRootItem(saleItem.uuid)) || []
            : sale?.sale_items?.filter((saleItem) => (saleItem.quantity > 0)) || [];
    }

    canConfirmSplit() {
        switch (this.options.type) {
            case 'by_covers':
                return this.options.covers! >= 2;
            case 'by_amount':
                return this.options.amount! >= 0.01;
            default:
                return false;
        }
    }

    private clearSplitSale() {
        delete this.originalSale;
        delete this.splitSale;

        this.canConfirmSale.set(false);
    }

    goBack() {
        switch (this.dialogMode()) {
            case 'type_options':
                this.dialogMode.set('type_select');
                this.options = {};
                break;
            case 'sale_view':
                this.clearSplitSale();

                switch (this.options.type) {
                    case 'by_covers':
                    case 'by_amount':
                        this.dialogMode.set('type_options');
                        break;
                    case 'by_items':
                    default:
                        this.dialogMode.set('type_select');
                        break;
                }
                break;
        }
    }

    async splitItem($event: Event, saleItem: SalesItems) {
        $event.stopPropagation();

        if (!this.originalSale || !this.splitSale) {
            return;
        }

        const answer = await this.promptDialogService.openDialog({
            data: {
                type: 'number',
                title: this.translateService.instant('CASHREGISTER.SPLIT_SALE.SPLIT_ITEM_PROMPT'),
                label: this.translateService.instant('CASHREGISTER.SPLIT_SALE.SPLIT_ITEM_PARTS'),
                min: 2,
                step: 1
            }
        });

        if (!answer) {
            return;
        }

        const insertIdx = (this.originalSale.sale_items || []).findIndex((si) => si === saleItem);
        const quantityPerItem = MathUtils.round(saleItem.quantity / answer, 3);
        const lastItemQuantity = MathUtils.round(saleItem.quantity - (quantityPerItem * (answer - 1)), 3);

        for (let i = 1; i <= (answer - 1); i++) {
            const newSi = structuredClone(saleItem);

            Object.assign(newSi, {
                quantity: quantityPerItem,
                id: undefined,
                uuid: generateUuid()
            });

            const newSplitSi = structuredClone(newSi);
            newSplitSi.quantity = 0;

            this.originalSale.sale_items?.splice(insertIdx, 0, newSi);
            this.splitSale.sale_items?.splice(insertIdx, 0, newSplitSi);
        }

        saleItem.quantity = lastItemQuantity;

        this.updateSalePrices();
    }

    openItemDetailsDialog($event: Event, saleItem: SalesItems) {
        $event.stopPropagation();

        const targetUuid = this.isRootItem(saleItem.uuid) ? dutchTreatGroupUuidPlaceholder : saleItem.uuid;
        const childrenItemUuids = this.originalSale?.sale_items?.filter((si) => si.sale_item_parent_uuid === targetUuid).map((si) => si.uuid) || [];
        const menuUuids = new Set(childrenItemUuids);

        let hasAddedUuids = false;

        do {
            hasAddedUuids = false;

            for (const saleItem of this.originalSale?.sale_items ?? []) {
                if (saleItem.sale_item_parent_uuid && menuUuids.has(saleItem.sale_item_parent_uuid) && !menuUuids.has(saleItem.uuid)) {
                    menuUuids.add(saleItem.uuid);
                    hasAddedUuids = true;
                }
            }
        } while (hasAddedUuids);

        const saleItemsToShow = structuredClone(this.originalSale?.sale_items?.filter((saleItem) => menuUuids.has(saleItem.uuid)) || []);

        for(const si of saleItemsToShow) {
            si.quantity = MathUtils.round(si.quantity * saleItem.quantity, 3);
        }

        this.itemDetailsDialogService.openDialog({
            data: {
                saleItems: saleItemsToShow
            }
        });
    }

    reduceRootItems(originalRootItems: SalesItems[]): SplitSaleRootItemsData | undefined {
        const firstRootItem = originalRootItems[0];

        if (!firstRootItem) {
            return;
        }

        return {
            name: firstRootItem.name!,
            price: firstRootItem.price,
            quantities: originalRootItems.filter(ri => ri.quantity > 0).map(ri => ri.quantity * 100)
        };
    }

    getRootItemsData(originalSale: SalesCashregister) {
        const lastFinalAmount = originalSale._split_sale_data?.last_sale_final_amount;

        if(lastFinalAmount == null || MathUtils.round(lastFinalAmount) === MathUtils.round(originalSale.final_amount || 0)) {
            return originalSale._split_sale_data?.root_items;
        }
    }

    confirm() {
        if (!this.originalSale || !this.splitSale) {
            return;
        }

        //Check if the original sale has sale items. If not this is the last sale.
        const isLastSale = this.dutchTreatSplitMode
            ? !this.originalSale.sale_items?.some((si) => this.isRootItem(si.uuid) && si.quantity !== 0)
            : !this.originalSale.sale_items?.some((si) => si.quantity !== 0);

        const originalRootItems = removeFromArray(this.originalSale.sale_items || [], (si) => this.isRootItem(si.uuid));
        const splitRootItems = removeFromArray(this.splitSale.sale_items || [], (si) => this.isRootItem(si.uuid));
        const movedRatio = MathUtils.round((splitRootItems.reduce((acc, si) => acc + si.quantity, 0) || 0), rootItemsRoundingDigits);

        if (this.dutchTreatSplitMode) {
            const originalTotalAmount = originalRootItems[0]?.price || 0;
            const targetSplitAmount = MathUtils.round(originalTotalAmount * movedRatio, 2);

            const splitSaleItemsByUuid = keyBy(this.splitSale.sale_items || [], (si) => si.uuid);

            const realItems = this.originalSale.sale_items || [];

            let currentAllocatedAmount = 0;

            for (const si of realItems) {
                const splitSi = splitSaleItemsByUuid[si.uuid];

                if (!splitSi) {
                    continue;
                }

                const previousQuantity = this.originalQuantitiesByUuid[si.uuid] || si.quantity;

                splitSi.quantity = isLastSale
                    ? previousQuantity //Put all of the remaining quantity into the split sale item
                    : MathUtils.round(si.quantity * movedRatio, 3); // Standard proportional allocation
                
                si.quantity = MathUtils.round(previousQuantity - splitSi.quantity, 3);

                const itemRowPrice = MathUtils.round(splitSi.quantity * si.final_price, 2);

                currentAllocatedAmount = MathUtils.round(currentAllocatedAmount + itemRowPrice, 2);
            }

            const remainingAmount = MathUtils.round(targetSplitAmount - currentAllocatedAmount, 2);
    
            if (remainingAmount) {
                const targetSaleItem = maxBy(this.splitSale.sale_items || [], (si) => si.not_discountable ? 0 : si.price * si.quantity);
    
                if (targetSaleItem) {
                    const priceChange = this.saleUtilsService.getGenericPriceChange(targetSaleItem, remainingAmount > 0 ? 'surcharge_fix' : 'discount_fix', Math.abs(remainingAmount), 'Arr conto separato');

                    targetSaleItem.price_changes = targetSaleItem.price_changes || [];
                    targetSaleItem.price_changes.push(priceChange)
                } 
            }
        }

        const originalSaleTransaction = this.options.type === 'by_items' || this.dutchTreatSplitMode
            ? this.saleTransactionUtils.createSaleTransaction(this.originalSale, this.operatorManagerService.getOperatorData())
            : null

        // Compile transaction with the changes for the original sale
        if (originalSaleTransaction) {
            originalSaleTransaction.skip_printing = true;

            const originalSaleItemsByUuid = keyBy(this.sale.sale_items ?? [], (si) => si.uuid);

            const targetSaleItems = isLastSale
                ? this.splitSale.sale_items || []
                : this.originalSale.sale_items || [];

            const isDutchOrNotLast = !isLastSale || this.dutchTreatSplitMode;

            for (const saleItem of targetSaleItems) {
                const originalSaleItem = originalSaleItemsByUuid[saleItem.uuid];
                const saleItemTransaction = this.saleTransactionUtils.createSaleItemTransaction(saleItem);

                if (!originalSaleItem) {
                    //Item was added during the split sale (via splitItem method), so add it to the original sale
                    if (isDutchOrNotLast) {
                        const { quantity, ...itemDetails } = saleItem;

                        saleItemTransaction.quantity_difference = saleItem.quantity;
                        saleItemTransaction.sale_item_details = itemDetails;
                    }
                } else {
                    if (isDutchOrNotLast) {
                        const quantityDifference = MathUtils.round(saleItem.quantity - originalSaleItem.quantity, 3);

                        if (quantityDifference) {
                            //Item quantity was changed during the split sale
                            saleItemTransaction.quantity_difference = quantityDifference;
                        }

                        if (quantityDifference || this.dutchTreatSplitMode) {
                            //Update price changes since fixed discount/surcharges are removed
                            saleItemTransaction.sale_item_details = {
                                original_quantity: saleItem.original_quantity,
                                price_changes: saleItem.price_changes
                            };
                        }
                    } else {
                        saleItemTransaction.sale_item_details = {
                            original_quantity: saleItem.original_quantity
                        };
                    }
                }

                // Add item transaction only if there are changes
                const hasDetails = Object.keys(saleItemTransaction.sale_item_details || {}).length;

                if (saleItemTransaction.quantity_difference || hasDetails) {
                    originalSaleTransaction.sale_items_transactions?.push(saleItemTransaction);
                }
            }

            const saleDetails: SaleBase = {};

            if (this.originalSale.covers != null) {
                const originalCovers = this.sale._split_sale_data?.original_covers ?? this.originalSale.covers;
                const usedCovers = this.sale._split_sale_data?.used_covers ?? 0;
                const remainingCovers = Math.max(0, originalCovers - usedCovers);
                let splitSaleCovers = 0;
                
                if (!isLastSale) {
                    // Calculates the covers without exceeding the still available ones
                    const calculatedCovers = Math.round(originalCovers * movedRatio) || 1;

                    this.splitSale.covers = splitSaleCovers = Math.min(remainingCovers, calculatedCovers);
                    saleDetails.covers = MathUtils.round(remainingCovers - splitSaleCovers);
                }
            }

            if (isLastSale) {
                saleDetails.name = this.getNewSaleName(this.originalSale.name);
                saleDetails.price_changes = this.originalSale.price_changes;
            }

            if (!this.originalSale.split_sale_origin_uuid) {
                saleDetails.split_sale_origin_uuid = this.originalSale.uuid;
            }

            originalSaleTransaction.sale_details = saleDetails;
        }

        this.originalSale.sale_items = this.originalSale.sale_items?.filter((item) => item.quantity !== 0) || [];
        this.splitSale.sale_items = this.splitSale.sale_items?.filter((item) => item.quantity !== 0 && !this.isRootItem(item.uuid)) || [];

        this.removeSaleItemRelations(this.splitSale);

        let splitSaleData: SplitSaleData | undefined;

        if(this.dutchTreatSplitMode) {
            this.splitSale._split_sale_data = {
                split_mode: this.dutchTreatSplitMode
            };
        }

        if(!isLastSale) {
            splitSaleData = {
                split_mode: this.dutchTreatSplitMode,
                split_index: this.splitIndex,
                type: 'by_items'
            };

            if(originalSaleTransaction?.sale_details?.covers != null) {
                splitSaleData.original_covers = this.sale._split_sale_data?.original_covers ?? this.sale.covers;
            }

            // Add used covers to the split sale data
            if(this.splitSale.covers != null) {
                splitSaleData.used_covers = (this.sale._split_sale_data?.used_covers ?? 0) + this.splitSale.covers;
            }
        }

        //For dutch treat split, keep only the root item changes in the transaction
        if (splitSaleData?.split_mode) {
            splitSaleData.root_items = this.reduceRootItems(originalRootItems);
        }

        if(originalSaleTransaction) {
            const saleDetails = originalSaleTransaction.sale_details = originalSaleTransaction.sale_details || {};

            //@ts-expect-error
            saleDetails._split_sale_data = splitSaleData;
        } else {
            this.originalSale._split_sale_data = splitSaleData;
        }

        if(!isLastSale) {
            this.updateNewSaleData(this.splitSale);
        }

        this.updateSalePrices();

        if(splitSaleData?.root_items) {
            splitSaleData.last_sale_final_amount = this.originalSale.final_amount;
        }

        const result: SplitSaleReturnValue = originalSaleTransaction
            ? {
                originalSaleTransaction: originalSaleTransaction,
                ...(!isLastSale && { splitSale: this.splitSale })
            }
            : {
                originalSale: this.originalSale,
                splitSale: this.splitSale
            }

        this.matDialogRef.close(result);
    }

    checkFixedDiscounts() {
        this.saleAmount = this.sale.final_amount || 0;

        if (this.options.type !== 'by_items' && !(this.options.type === 'by_covers' && this.splitItemsOnDutchTreat)) {
            return;
        }

        const saleFixPriceChanges = (this.sale.price_changes || [])
            .filter((pc) => pc.type.endsWith('_fix'));

            const saleItemsFixPriceChanges = (this.sale.sale_items || [])
            .map((si) => (si.price_changes || [])
                .filter((pc) => pc.type.endsWith('_fix')))
            .flat();

        if ((saleFixPriceChanges.length > 0) || (saleItemsFixPriceChanges.length > 0)) {
            this.alertDialogService.openDialog({data: {messageLabel: 'CASHREGISTER.SPLIT_SALE.DIALOG_FIXED_DISCOUNTS'}});

            const fixPriceChangesAmount = saleFixPriceChanges.reduce((acc, pc) => acc + (pc.amount || 0), 0) + saleItemsFixPriceChanges.reduce((acc, pc) => acc + (pc.amount || 0), 0);
            this.saleAmount = MathUtils.round((this.sale.final_amount || 0) - fixPriceChangesAmount);
        }
    }

    prepareSplitByItems() {
        this.originalSale = structuredClone(this.sale);

        if (!this.originalSale.sale_items) {
            return;
        }

        this.prepareSplitSale();
    }

    splitByItems() {
        this.options.type = 'by_items';
        this.checkFixedDiscounts();
        this.prepareSplitByItems();
    }

    getCountryDefaultVat() {
        switch (this.configurationManagerService.getShopCountry()) {
            case 'IT':
                return 10;
            case 'DE':
                return 7;
            case 'ES':
                return 7;
            case 'FR':
                return null;
            default:
                return 10;
        }
    }

    async splitByCoversOrAmount(type: 'by_covers' | 'by_amount') {
        if (this.disableAmountAndCovers) {
            return;
        }

        const covers = this.sale.covers || 2;

        this.options = {
            type: type,
            covers: covers,
            itemName: this.configurationManagerService.getPreference('cashregister.split_sale.default_item_name') || this.translateService.instant('CASHREGISTER.SPLIT_SALE.DEFAULT_ITEM_NAME')
        };

        this.checkFixedDiscounts();

        const defaultDepId = parseInt(this.configurationManagerService.getPreference('cashregister.split_sale.default_department_id') || '');

        if (defaultDepId) {
            this.options.department = this.departments.find((dep) => dep.id === defaultDepId);
        } else {
            const countryDefaultVat = this.getCountryDefaultVat();

            if (countryDefaultVat !== null) {
                this.options.department = this.departments.find((dep) => dep.vat && dep.vat.value === countryDefaultVat);
            }
        }

        this.dialogMode.set("type_options");

        if (!this.showPrices) {
            if (this.options.amount) {
                this.hideAsterisk = true;
            } else {
                this.hideAsterisk = false;
            }
        }
    }

    previewPeopleTotal() {
        if (!this.sale.final_amount || !this.options.covers) {
            return;
        }

        return (this.options.type === 'by_amount')
            ? this.options.amount
            : MathUtils.round(this.saleAmount / this.options.covers);
    }

    async confirmSplitOptions() {
        if (this.options.type === 'by_items') {
            return;
        }

        if (this.splitItemsOnDutchTreat) {
            return this.prepareSplitByItems();
        }

        //Create split sale template
        const newSale: Sales = {
            ...(await this.saleUtilsService.getSaleTemplate({ skipUpdateProgressive: true })),
            assigned_id: this.sale.assigned_id,
            assigned_name: this.sale.assigned_name,
            name: this.sale.name,
            channel: this.sale.channel,
            external_id: this.sale.external_id,
            order_id: this.sale.order_id,
            order_uuid: this.sale.order_uuid,
            room_id: this.sale.room_id,
            room_name: this.sale.room_name,
            sale_items: await this.saleUtilsService.repartitionSaleItemsByDepartment(this.sale, this.options.type!, this.options.type === 'by_amount' ? this.options.amount! : this.options.covers!, this.options.department ?? undefined),
            sale_number: this.sale.sale_number,
            sale_parent_uuid: this.sale.uuid,
            seller_id: this.sale.seller_id,
            seller_name: this.sale.seller_name,
            split_sale_origin_uuid: this.sale.uuid,
            table_id: this.sale.table_id,
            table_name: this.sale.table_name,
        };

        for (const saleItem of newSale.sale_items || []) {
            saleItem.name = this.options.itemName || saleItem.name;
            saleItem.seller_id = newSale.seller_id;
            saleItem.seller_name = newSale.seller_name;
        }

        this.saleUtilsService.calculateSalePrices(newSale);

        this.originalSale = newSale;

        this.prepareSplitSale();
    }

    onPriceChange(event: any) {
        if(this.showPrices) {
            return;
        }

        const userInput = event;

        if (userInput) {
            this.hideAsterisk = false;

            setTimeout(() => {
                this.hideAsterisk = true;
            }, 1000);
        } else {
            this.hideAsterisk = false;
        }
    }

    swipeRight(saleItem: SalesItems) {
        this.moveToSplitSale(saleItem);
    }

    swipeLeft(saleItem: SalesItems) {
        this.moveToOriginalSale(saleItem);
    }
}

@Injectable({
    providedIn: "root",
})
export class SplitSaleDialogService extends BaseDialogService {
    private readonly dialogRef = inject(MatDialog);

    public async openDialog(config: NonNullableConfigData<SplitSaleDialogData>): Promise<SplitSaleReturnValue | undefined> {
        const data: SplitSaleDialogData = config.data;

        return await lastValueFrom(this.dialogRef.open(SplitSaleDialogComponent, {
            ...config,
            ...this.switchMobileDesktopDimensions({ width: "1000px" }, { fullScreenForMobile: true }),
            disableClose: true,
            data,
        }).afterClosed()) || undefined;
    }
}
